import React, { useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Typography, LinearProgress, Button, Divider, Zoom } from '@mui/material';
import { LoadingButton, TextButton } from '../../common/StyledButtons';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { SUBMISSION_TYPES, SUBMISSION_TYPES_DESCRIPTIONS, SUBMISSION_TYPES_HUMAN_MAP, postSubmission } from '../../../actions/external/SubmissionActions';
import { showNotificationBar, NOTIFICATION_TYPE } from '../../../actions/error-pages/NotificationBarActions';
import { getUserId } from '../../../selectors/UserSelectors';
import { getAllSubmissionsForAdmin } from '../../../actions/internal/AdminActions';
import { StyledMultilineTextField, StyledTextField } from '../../common/StyledTextField';
import { CheckCircleOutlineOutlined } from '@mui/icons-material';
import { addSiteSubmission } from '../../../actions/internal/SiteActions';
import StyledDialog from '../../common/StyledDialog';
import { STANDARD_PARAGRAPH_STYLE } from '../../../constants/Styles';

const AddRequestModal = ({ open, onClose, siteId, options }) => {
  const [step, setStep] = useState(1);
  const [requestType, setRequestType] = useState('');
  const [values, setValues] = useState({ title: '', description: '' });
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const setFieldValue = (id, value) => setValues({ ...values, [id]: value });

  useEffect(() => {
    if (open && options) {
      if (options.request_type) setRequestType(options.request_type);
      if (options.step) setStep(options.step);
    }

  }, [open]);

  const clearForm = () => {
    setRequestType('');
    setValues({ title: '', description: '' });
    setErrors([]);
    setStep(1);
    setShowThankYou(false);
  };

  const handleClose = () => {
    clearForm();
    onClose();
  };

  const validateFields = () => {
    const missing = [];
    if (!requestType) missing.push('requestType');
    if (!values.title) missing.push('title');
    if (!values.description) missing.push('description');
    return missing;
  };

  const handleAddRequest = async () => {
    const validationErrors = validateFields();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setShowThankYou(true);
    setIsLoading(true);

    try {
      await addSiteSubmission(getUserId(), siteId, requestType, values);

      setTimeout(() => {
        showNotificationBar(NOTIFICATION_TYPE.SUCCESS, 'Your request has been created successfully!');
        setIsLoading(false);
        handleClose();
      }, 5000);
    } catch (ex) {
      console.error(ex);
      showNotificationBar(NOTIFICATION_TYPE.ERROR, 'An error occurred. Please try again or contact support.');
      setIsLoading(false);
    }
  };

  return (
    <StyledDialog fullWidth open={open} maxWidth="sm">
      <DialogTitle>
            <h4 style={{ margin: 0, padding: 0 }}>Open New Request</h4>
      </DialogTitle>
      <DialogContent>
        {showThankYou ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <Zoom in={true} style={{ transitionDelay: '200ms' }}>
              <CheckCircleOutlineOutlined sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
            </Zoom>
            <h4 style={{ margin: 0, padding: 0, fontSize: '28px' }}>Submitting Request!</h4>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
              Your request is on its way! We’ll email you shortly to confirm. We can’t wait to help you out.
            </Typography>
            <LinearProgress sx={{ width: '100%' }} />
          </Box>
        ) : step === 1 ? (
          <Box>
            <p sx={{ mb: 2 }}>What type of request would you like to open?</p>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Object.keys(SUBMISSION_TYPES).map((key) => (
                <Button
                  key={key}
                  variant={requestType === key ? 'contained' : 'outlined'}
                  sx={{
                    borderRadius: '12px',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    p: 2,
                    boxShadow: requestType === key ? '0 4px 12px rgba(76, 175, 80, 0.3)' : '0 2px 6px rgba(0,0,0,0.1)',
                    backgroundColor: requestType === key ? '#4caf50' : '#f9f9f9',
                    color: requestType === key ? '#fff' : '#333',
                    border: '1px solid',
                    borderColor: requestType === key ? '#4caf50' : '#ddd',
                    transition: 'all 0.3s',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: requestType === key ? '#43a047' : '#f0f0f0',
                    },
                  }}
                  onClick={() => {
                    setRequestType(key);
                    setStep(2);
                  }}
                  fullWidth
                >
                  <Box>
                    <h4 style={{ margin: 0, padding: 0, fontSize: '20px' }}>{SUBMISSION_TYPES_HUMAN_MAP[key]}</h4>
                    <p style={{ ...STANDARD_PARAGRAPH_STYLE }}>
                      {SUBMISSION_TYPES_DESCRIPTIONS[key]}
                    </p>
                  </Box>
                </Button>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{
              backgroundColor: '#f1f8e9',
              borderRadius: '8px',
              p: 2,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: 0, padding: 0, fontSize: '20px', fontWeight: 600, color: '#558b2f' }}>You're opening a {SUBMISSION_TYPES_HUMAN_MAP[requestType].toLocaleLowerCase()}</h4>
            </Box>
            <Divider />
            <Box>
              <h4 style={{ margin: 0, padding: 0, fontSize: '20px' }}>Title <span style={{fontSize: '16px'}}>({values.title.length}/250 characters)</span></h4>
              <StyledTextField
                autoFocus
                placeholder="Enter a clear, short title"
                value={values.title}
                onChange={(e) => setFieldValue('title', e.target.value.slice(0, 250))}
                helperText='Example: "Update homepage banner" or "Issue with checkout flow"'
                error={errors.includes('title')}
              />
            </Box>
            <Box>
              <h4 style={{ margin: 0, padding: 0, fontSize: '20px' }}>Description <span style={{fontSize: '16px'}}>({values.description.length}/7000 characters)</span></h4>
              <StyledMultilineTextField
                placeholder="Describe what you need help with"
                value={values.description}
                onChange={(e) => setFieldValue('description', e.target.value.slice(0, 7000))}
                helperText='Include as much relevant detail as possible (e.g., what you want changed, where it happens, any examples or URLs).'
                error={errors.includes('description')}
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      {!showThankYou && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
          <TextButton onClick={handleClose} disabled={isLoading}>Cancel</TextButton>
          <LoadingButton onClick={handleAddRequest} isLoading={isLoading} disabled={step !== 2}>
            Submit
          </LoadingButton>
        </Box>
      )}
    </StyledDialog>
  );
};

export default observer(withRouter(AddRequestModal));